import React from "react";
import { Drawer } from "antd";
import moment from "moment";
import { IBookingPackage } from "../../../common/types";
import { BookingDetailsWrapper, BookingHighlight } from "./Customer.style";

interface ICustomerBookingDetailsProps {
  booking: IBookingPackage | null;
  open: boolean;
  onClose: () => void;
}

const formatDate = (value: any, fallback = "—") => {
  if (!value) {
    return fallback;
  }
  if (moment.isMoment(value)) {
    return value.format("DD/MM/YYYY");
  }
  if (typeof value?.toDate === "function") {
    return moment(value.toDate()).format("DD/MM/YYYY");
  }
  return moment(value).format("DD/MM/YYYY");
};

const formatAmount = (value?: number) =>
  typeof value === "number"
    ? new Intl.NumberFormat("el-GR", {
        style: "currency",
        currency: "EUR",
      }).format(value)
    : "—";

export const CustomerBookingDetails = ({
  booking,
  open,
  onClose,
}: ICustomerBookingDetailsProps) => {
  const mainGuest =
    booking?.passengers?.find((passenger) => passenger.type === "ADULT") ||
    booking?.passengers?.[0];
  const primaryHotel = booking?.packageHotels?.[0];
  const primaryHotelName =
    (primaryHotel as any)?.hotelName ||
    (primaryHotel as any)?.name ||
    booking?.travelPackage?.name ||
    "—";
  const checkInDate =
    (primaryHotel as any)?.checkInDate || booking?.bookingDate;
  const checkOutDate =
    (primaryHotel as any)?.checkOutDate || booking?.bookingDate;
  const mealPlan =
    (primaryHotel as any)?.meal || (primaryHotel as any)?.mealsType || "—";
  return (
    <Drawer
      width={780}
      title="Πληροφορίες κράτησης"
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {booking ? (
        <BookingDetailsWrapper>
          <div className="details-grid">
            <div className="details-block">
              <strong>Status</strong>
              <span>{booking.status}</span>
            </div>
            <div className="details-block">
              <strong>Hotel</strong>
              <span>{primaryHotelName}</span>
            </div>
            <div className="details-block">
              <strong>Booking ID</strong>
              <span>PFH-{booking.bookingId}</span>
            </div>
            <div className="details-block">
              <strong>Payment status</strong>
              <span>{booking.paymentStatus}</span>
            </div>
            <div className="details-block">
              <strong>Total amount</strong>
              <span>{formatAmount(booking.totalPrice)}</span>
            </div>
            <div className="details-block">
              <strong>Πληρωμή</strong>
              <span>
                {booking.allowPartialPayment
                  ? `${formatAmount(
                      booking.totalPartialPayment,
                    )} προκαταβολή / ${formatAmount(
                      booking.totalRemainingAmount,
                    )} υπόλοιπο`
                  : "Εφάπαξ πληρωμή"}
              </span>
            </div>
          </div>
          <table className="details-table" style={{ marginTop: 24 }}>
            <thead>
              <tr>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Adults</th>
                <th>Children</th>
                <th>Infants</th>
                <th>Meal type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{formatDate(checkInDate)}</td>
                <td>{formatDate(checkOutDate)}</td>
                <td>
                  {booking?.passengers?.filter(
                    (passenger) => passenger.type === "ADULT",
                  ).length || 0}
                </td>
                <td>
                  {booking?.passengers?.filter(
                    (passenger) => passenger.type === "CHILD",
                  ).length || 0}
                </td>
                <td>
                  {booking?.passengers?.filter(
                    (passenger) =>
                      passenger.type !== "ADULT" && passenger.type !== "CHILD",
                  ).length || 0}
                </td>
                <td>{mealPlan}</td>
              </tr>
            </tbody>
          </table>
          {mainGuest && (
            <div style={{ marginTop: 32 }}>
              <h4>Main guest</h4>
              <table className="details-table">
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>
                      {mainGuest.firstName} {mainGuest.lastName}
                    </td>
                  </tr>
                  <tr>
                    <th>Date of birth</th>
                    <td>{formatDate(mainGuest.dateOfBirth)}</td>
                  </tr>
                  <tr>
                    <th>ID number</th>
                    <td>{booking.customer?.iDPassportNumber || "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {booking.status === "CANCELED" || booking.isCanceled ? (
            <BookingHighlight>
              <h4>Ακυρωμένη κράτηση</h4>
              <div>
                Η κράτηση ακυρώθηκε στις{" "}
                {formatDate(
                  booking.cancellation?.canceledDate || booking.bookingDate,
                  "",
                ) || "—"}
              </div>
              {booking.cancellation?.cancellationReason && (
                <div style={{ marginTop: 8 }}>
                  Αιτία: {booking.cancellation.cancellationReason}
                </div>
              )}
            </BookingHighlight>
          ) : null}
        </BookingDetailsWrapper>
      ) : null}
    </Drawer>
  );
};
